import React, { useEffect, useMemo, useState } from "react";
import {
  ShoppingBag,
  ShoppingCart,
  Trash2,
  Minus,
  Plus,
  Check,
  ChevronRight,
  AlertTriangle,
  Package,
  ArrowRight,
  RefreshCw,
  Tag,
  Sparkles,
  CircleCheck,
  X,
} from "lucide-react";

import {
  useAddConfirmMutation,
  useGetTodosQuery,
  useDeleteTodoMutation,
  useUpdateTodoMutation,
} from "../../redux/slices/productApiSlice";

import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Basket = () => {
  const navigate = useNavigate();

  // =========================================================
  // CART - RTK QUERY
  // =========================================================

  const {
    data,
    isLoading,
    isError,
    error: basketQueryError,
    refetch,
  } = useGetTodosQuery();

  const [updateTodo] = useUpdateTodoMutation();
  const [addConfirm] = useAddConfirmMutation();
  const [removeTodo] = useDeleteTodoMutation();

  // =========================================================
  // LOCAL UI STATE
  // =========================================================

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [updatingProductId, setUpdatingProductId] = useState(null);
  const [removingProductId, setRemovingProductId] = useState(null);
  const [confirmingCart, setConfirmingCart] = useState(false);

  // =========================================================
  // ERROR MESSAGE
  // =========================================================

  const getErrorMessage = (err) => {
    const serverMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.data?.message ||
      err?.data?.message ||
      err?.data?.error;

    if (serverMessage) {
      return String(serverMessage);
    }

    if (
      err?.status === 400 ||
      err?.response?.status === 400
    ) {
      return "Göndərilən məlumatlar düzgün deyil. Zəhmət olmasa, yenidən cəhd edin.";
    }

    if (
      err?.status === 401 ||
      err?.response?.status === 401
    ) {
      return "Bu əməliyyatı yerinə yetirmək üçün hesabınıza daxil olmalısınız.";
    }

    if (
      err?.status === 403 ||
      err?.response?.status === 403
    ) {
      return "Bu əməliyyatı yerinə yetirmək üçün icazəniz yoxdur.";
    }

    if (
      err?.status === 404 ||
      err?.response?.status === 404
    ) {
      return "Məlumat tapılmadı. Məhsul artıq mövcud olmaya bilər.";
    }

    if (
      err?.status === 409 ||
      err?.response?.status === 409
    ) {
      return "Bu əməliyyat mümkün olmadı. Məhsulun məlumatları artıq dəyişmiş ola bilər.";
    }

    if (
      err?.status === 429 ||
      err?.response?.status === 429
    ) {
      return "Çox sayda sorğu göndərildi. Bir qədər gözləyib yenidən cəhd edin.";
    }

    if (
      err?.status >= 500 ||
      err?.response?.status >= 500
    ) {
      return "Serverdə texniki problem baş verdi. Zəhmət olmasa, bir qədər sonra yenidən cəhd edin.";
    }

    if (
      err?.message === "Network Error" ||
      err?.error === "FETCH_ERROR"
    ) {
      return "Serverə qoşulmaq mümkün olmadı. İnternet bağlantınızı və serverin işlək vəziyyətdə olduğunu yoxlayın.";
    }

    if (err?.message) {
      return err.message;
    }

    return "Naməlum xəta baş verdi. Zəhmət olmasa, yenidən cəhd edin.";
  };

  // =========================================================
  // PRODUCT SELECTION
  // =========================================================

  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    if (!Array.isArray(data) || data.length === 0) {
      return;
    }

    const allSelected = data.every((product) =>
      selectedProducts.includes(product._id)
    );

    if (allSelected) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(data.map((product) => product._id));
    }
  };

  // =========================================================
  // QUANTITY UPDATE
  // =========================================================

  const updateQuantity = async (
    productId,
    quantity
  ) => {
    if (!productId) {
      toast.error(
        "Məhsulun identifikatoru tapılmadı."
      );
      return;
    }

    if (quantity < 1) {
      return;
    }

    if (updatingProductId !== null) {
      return;
    }

    setUpdatingProductId(productId);

    try {
      await updateTodo({
        productId,
        quantity,
      }).unwrap();

      toast.success(
        "Məhsulun miqdarı uğurla yeniləndi."
      );
    } catch (err) {
      console.error(
        "Məhsulun miqdarı yenilənmədi:",
        err
      );

      toast.error(
        `Məhsulun miqdarını yeniləmək mümkün olmadı.\n\nSəbəb: ${getErrorMessage(
          err
        )}`
      );
    } finally {
      setUpdatingProductId(null);
    }
  };

  // =========================================================
  // REMOVE PRODUCT
  // =========================================================

  const removeProduct = async (_id) => {
    if (!_id) {
      toast.error(
        "Məhsulun identifikatoru tapılmadı."
      );
      return;
    }

    if (removingProductId !== null) {
      return;
    }

    setRemovingProductId(_id);

    try {
      await removeTodo(_id).unwrap();

      setSelectedProducts((prev) =>
        prev.filter((id) => id !== _id)
      );

      toast.success(
        "Məhsul səbətdən uğurla silindi."
      );
    } catch (err) {
      console.error(
        "Məhsul səbətdən silinmədi:",
        err
      );

      toast.error(
        `Məhsulu səbətdən silmək mümkün olmadı.\n\nSəbəb: ${getErrorMessage(
          err
        )}`
      );
    } finally {
      setRemovingProductId(null);
    }
  };

  // =========================================================
  // CONFIRM CART
  // =========================================================

  const handleConfirmCart = async () => {
    try {
      if (!Array.isArray(data)) {
        toast.error(
          "Səbətdəki məhsulların məlumatlarını əldə etmək mümkün olmadı. Zəhmət olmasa, yenidən cəhd edin."
        );
        return;
      }

      const selectedItems = data.filter((product) =>
        selectedProducts.includes(product._id)
      );

      if (selectedItems.length === 0) {
        toast.info(
          "Zəhmət olmasa, ödənişə keçmək üçün ən azı bir məhsul seçin."
        );
        return;
      }

      const unavailableProduct =
        selectedItems.find(
          (product) => product.stock === 0
        );

      if (unavailableProduct) {
        toast.warning(
          `"${unavailableProduct.title}" məhsulu hazırda stokda yoxdur. Bu məhsulu seçimdən çıxardıqdan sonra səbəti təsdiqləyə bilərsiniz.`
        );
        return;
      }

      setConfirmingCart(true);

      const response = await addConfirm({
        products: selectedItems,
      }).unwrap();

      const confirmedCartId =
        response.confirmedCartId;

      if (!confirmedCartId) {
        throw new Error(
          "Təsdiqlənmiş səbətin identifikatoru serverdən alınmadı."
        );
      }

      toast.success(
        "Səbət uğurla təsdiqləndi. Ödəniş səhifəsinə yönləndirilirsiniz."
      );

      setTimeout(() => {
        navigate(
          `/payment?confirmedCartId=${confirmedCartId}`
        );
      }, 1000);
    } catch (err) {
      console.error(
        "Səbət təsdiqlənərkən xəta baş verdi:",
        err
      );

      const reason = getErrorMessage(err);

      toast.error(
        `Səbəti təsdiqləmək mümkün olmadı.\n\nSəbəb: ${reason}`
      );
    } finally {
      setConfirmingCart(false);
    }
  };

  // =========================================================
  // SYNC SELECTED PRODUCTS
  // =========================================================

  useEffect(() => {
    if (!Array.isArray(data)) {
      return;
    }

    setSelectedProducts((prevSelected) => {
      const currentProductIds = data.map(
        (product) => product._id
      );

      if (currentProductIds.length === 0) {
        return [];
      }

      const validSelected = prevSelected.filter(
        (id) => currentProductIds.includes(id)
      );

      const newProductIds =
        currentProductIds.filter(
          (id) => !prevSelected.includes(id)
        );

      return [
        ...validSelected,
        ...newProductIds,
      ];
    });
  }, [data]);

  // =========================================================
  // CALCULATIONS
  // =========================================================

  const selectedItems = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.filter((product) =>
      selectedProducts.includes(product._id)
    );
  }, [data, selectedProducts]);

  const selectedCount = selectedItems.length;

  const totalQuantity = useMemo(() => {
    return selectedItems.reduce(
      (total, product) =>
        total + Number(product.quantity || 1),
      0
    );
  }, [selectedItems]);

  const calculateTotalPrice = useMemo(() => {
    return (
      Math.round(
        selectedItems.reduce(
          (total, product) =>
            total +
            (product.price || 0) *
              (product.quantity || 1),
          0
        ) * 100
      ) / 100
    );
  }, [selectedItems]);

  const calculateTotalOrginalPrice = useMemo(() => {
    return (
      Math.round(
        selectedItems.reduce(
          (total, product) =>
            total +
            (product.orginalPrice ||
              product.price ||
              0) *
              (product.quantity || 1),
          0
        ) * 100
      ) / 100
    );
  }, [selectedItems]);

  const discountAmount = Math.max(
    0,
    calculateTotalOrginalPrice -
      calculateTotalPrice
  );

  const isStockAvailable =
    selectedItems.length > 0 &&
    !selectedItems.some(
      (product) => product.stock === 0
    );

  const allSelected =
    Array.isArray(data) &&
    data.length > 0 &&
    data.every((product) =>
      selectedProducts.includes(product._id)
    );

  // =========================================================
  // LOADING
  // =========================================================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f6f7fb] dark:bg-[#08090b] px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 h-10 w-48 animate-pulse rounded-xl bg-gray-200 dark:bg-white/10" />

          <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
            <div className="space-y-4">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-40 animate-pulse rounded-3xl bg-white shadow-sm dark:bg-[#111318]"
                />
              ))}
            </div>

            <div className="hidden h-80 animate-pulse rounded-3xl bg-white lg:block dark:bg-[#111318]" />
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // ERROR
  // =========================================================

  if (isError) {
    const queryErrorMessage =
      getErrorMessage(basketQueryError);

    return (
      <div className="min-h-screen bg-[#f6f7fb] px-4 py-10 dark:bg-[#08090b]">
        <div className="mx-auto flex min-h-[60vh] max-w-lg items-center justify-center">
          <div className="w-full overflow-hidden rounded-[32px] border border-red-100 bg-white p-7 text-center shadow-xl shadow-red-100/40 dark:border-red-900/40 dark:bg-[#111318] dark:shadow-none sm:p-10">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-red-50 text-red-500 dark:bg-red-950/40">
              <AlertTriangle size={36} />
            </div>

            <h2 className="text-2xl font-black tracking-tight text-gray-900 dark:text-white">
              Səbət yüklənmədi
            </h2>

            <p className="mt-3 text-sm leading-6 text-gray-500 dark:text-gray-400">
              Səbət məlumatlarını əldə etmək
              mümkün olmadı.
            </p>

            <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 p-4 text-left dark:border-red-900/30 dark:bg-red-950/20">
              <p className="mb-1 text-xs font-bold uppercase tracking-wider text-red-500">
                Xəta
              </p>

              <p className="break-words text-sm leading-6 text-red-700 dark:text-red-300">
                {queryErrorMessage}
              </p>
            </div>

            <button
              type="button"
              onClick={() => refetch()}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-gray-900 px-5 py-3.5 text-sm font-bold text-white transition hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              <RefreshCw size={17} />
              Yenidən cəhd et
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // EMPTY CART
  // =========================================================

  if (
    Array.isArray(data) &&
    data.length === 0
  ) {
    return (
      <div className="min-h-screen bg-[#f6f7fb] px-4 py-8 dark:bg-[#08090b] sm:px-6">
        <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center justify-center">
          <div className="w-full text-center">
            <div className="relative mx-auto mb-8 flex h-32 w-32 items-center justify-center rounded-[38px] bg-white shadow-2xl shadow-gray-200/70 dark:bg-[#111318] dark:shadow-none">
              <div className="absolute inset-3 rounded-[30px] bg-gradient-to-br from-indigo-500/10 to-purple-500/10" />

              <ShoppingBag
                size={55}
                strokeWidth={1.5}
                className="relative text-indigo-500"
              />

              <span className="absolute right-5 top-5 flex h-7 w-7 items-center justify-center rounded-full bg-indigo-500 text-xs font-black text-white">
                0
              </span>
            </div>

            <p className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-indigo-500">
              Səbət
            </p>

            <h1 className="text-3xl font-black tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Səbətiniz boşdur
            </h1>

            <p className="mx-auto mt-4 max-w-md text-sm leading-7 text-gray-500 dark:text-gray-400">
              Bəyəndiyiniz məhsulları səbətə
              əlavə etdikdə onlar burada
              görünəcək.
            </p>

            <button
              type="button"
              onClick={() => navigate("/products")}
              className="mx-auto mt-8 flex items-center justify-center gap-2 rounded-2xl bg-gray-900 px-7 py-4 text-sm font-bold text-white shadow-xl shadow-gray-300/30 transition hover:-translate-y-0.5 hover:bg-black dark:bg-white dark:text-gray-900 dark:shadow-none dark:hover:bg-gray-100"
            >
              Məhsullara bax
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="min-h-screen bg-[#f6f7fb] px-3 pb-32 pt-5 dark:bg-[#08090b] sm:px-5 sm:pt-8 lg:px-8 lg:pb-12">
      <div className="mx-auto max-w-7xl">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-indigo-500">
              <ShoppingCart size={15} />
              Sifariş
            </div>

            <h1 className="text-3xl font-black tracking-tight text-gray-950 dark:text-white sm:text-4xl">
              Səbətim
            </h1>

            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {data.length} məhsul · {totalQuantity} ədəd
            </p>
          </div>

          <button
            type="button"
            onClick={toggleSelectAll}
            className="flex w-fit items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-bold text-gray-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-600 dark:border-white/10 dark:bg-[#111318] dark:text-gray-300 dark:hover:text-indigo-400"
          >
            {allSelected ? (
              <>
                <X size={15} />
                Seçimi təmizlə
              </>
            ) : (
              <>
                <Check size={15} />
                Hamısını seç
              </>
            )}
          </button>
        </div>

        {/* =====================================================
            MAIN GRID
        ====================================================== */}

        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_370px]">

          {/* ===================================================
              PRODUCTS
          ==================================================== */}

          <section className="space-y-3">

            {data.map((product) => {
              const isSelected =
                selectedProducts.includes(
                  product._id
                );

              const isUpdating =
                updatingProductId ===
                product.productId;

              const isRemoving =
                removingProductId ===
                product._id;

              const image =
                Array.isArray(product.photo) &&
                product.photo.length > 0
                  ? product.photo[0]
                  : product.photo;

              const productOriginalPrice =
                product.orginalPrice ||
                product.price ||
                0;

              const productPrice =
                product.price || 0;

              const hasDiscount =
                productOriginalPrice >
                productPrice;

              return (
                <article
                  key={product._id}
                  className={`group relative overflow-hidden rounded-[24px] border bg-white transition-all duration-300 dark:bg-[#111318] ${
                    isSelected
                      ? "border-indigo-200 shadow-lg shadow-indigo-100/50 dark:border-indigo-500/30 dark:shadow-none"
                      : "border-gray-200/80 shadow-sm hover:shadow-md dark:border-white/[0.06]"
                  }`}
                >
                  {/* selected indicator */}

                  {isSelected && (
                    <div className="absolute left-0 top-0 h-full w-1 bg-indigo-500" />
                  )}

                  <div className="flex gap-3 p-3 sm:gap-5 sm:p-5">

                    {/* CHECK */}

                    <div className="flex shrink-0 items-start pt-1">
                      <button
                        type="button"
                        disabled={
                          isUpdating ||
                          isRemoving ||
                          confirmingCart
                        }
                        onClick={() =>
                          toggleProductSelection(
                            product._id
                          )
                        }
                        className={`flex h-6 w-6 items-center justify-center rounded-lg border-2 transition-all ${
                          isSelected
                            ? "border-indigo-500 bg-indigo-500 text-white"
                            : "border-gray-300 bg-white dark:border-gray-600 dark:bg-transparent"
                        }`}
                        aria-label={`${product.title} məhsulunu seç`}
                      >
                        {isSelected && (
                          <Check
                            size={14}
                            strokeWidth={3}
                          />
                        )}
                      </button>
                    </div>

                    {/* IMAGE */}

                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/product/${product.productId}`
                        )
                      }
                      className="relative h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-gray-100 sm:h-32 sm:w-32 dark:bg-[#0b0c0f]"
                    >
                      {image ? (
                        <img
                          src={image}
                          alt={product.title}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-300 dark:text-gray-700">
                          <Package size={32} />
                        </div>
                      )}

                      {hasDiscount && (
                        <span className="absolute left-2 top-2 rounded-lg bg-rose-500 px-2 py-1 text-[9px] font-black text-white shadow-lg">
                          ENDİRİM
                        </span>
                      )}
                    </button>

                    {/* CONTENT */}

                    <div className="min-w-0 flex-1">

                      {/* title + delete */}

                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/product/${product.productId}`
                            )
                          }
                          className="min-w-0 text-left"
                        >
                          <h2 className="line-clamp-2 text-sm font-black leading-5 text-gray-900 transition hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400 sm:text-base">
                            {product.title}
                          </h2>
                        </button>

                        <button
                          type="button"
                          disabled={
                            isRemoving ||
                            isUpdating ||
                            confirmingCart
                          }
                          onClick={() =>
                            removeProduct(
                              product._id
                            )
                          }
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-gray-400 transition hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-950/30"
                          aria-label="Məhsulu sil"
                        >
                          {isRemoving ? (
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-red-500" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>

                      {/* stock */}

                      <div className="mt-2">
                        {product.stock === 0 ? (
                          <span className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-2 py-1 text-[10px] font-bold text-red-600 dark:bg-red-950/30 dark:text-red-400">
                            <AlertTriangle size={12} />
                            Stokda yoxdur
                          </span>
                        ) : product.stock === 1 ? (
                          <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-600 dark:bg-amber-950/30 dark:text-amber-400">
                            <AlertTriangle size={12} />
                            Son 1 ədəd
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
                            <CircleCheck size={12} />
                            Stokda var
                          </span>
                        )}
                      </div>

                      {/* bottom */}

                      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">

                        {/* price */}

                        <div>
                          {hasDiscount && (
                            <div className="mb-0.5 text-[11px] font-medium text-gray-400 line-through">
                              {(
                                productOriginalPrice *
                                (product.quantity || 1)
                              ).toFixed(2)}{" "}
                              ₼
                            </div>
                          )}

                          <div className="text-lg font-black tracking-tight text-gray-950 dark:text-white sm:text-xl">
                            {(
                              productPrice *
                              (product.quantity || 1)
                            ).toFixed(2)}{" "}
                            ₼
                          </div>
                        </div>

                        {/* quantity */}

                        <div className="flex h-9 items-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-[#0b0c0f]">

                          <button
                            type="button"
                            disabled={
                              isUpdating ||
                              isRemoving ||
                              confirmingCart
                            }
                            onClick={() =>
                              product.quantity > 1
                                ? updateQuantity(
                                    product.productId,
                                    product.quantity - 1
                                  )
                                : removeProduct(
                                    product._id
                                  )
                            }
                            className="flex h-full w-9 items-center justify-center text-gray-500 transition hover:bg-white hover:text-gray-900 disabled:opacity-40 dark:hover:bg-white/5 dark:hover:text-white"
                          >
                            {product.quantity > 1 ? (
                              <Minus size={14} />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>

                          <span className="flex min-w-[34px] justify-center text-xs font-black text-gray-900 dark:text-white">
                            {isUpdating ? (
                              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-500" />
                            ) : (
                              product.quantity
                            )}
                          </span>

                          <button
                            type="button"
                            disabled={
                              isUpdating ||
                              isRemoving ||
                              confirmingCart
                            }
                            onClick={() =>
                              updateQuantity(
                                product.productId,
                                product.quantity + 1
                              )
                            }
                            className="flex h-full w-9 items-center justify-center text-gray-500 transition hover:bg-white hover:text-gray-900 disabled:opacity-40 dark:hover:bg-white/5 dark:hover:text-white"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>

          {/* ===================================================
              SUMMARY DESKTOP
          ==================================================== */}

          <aside className="hidden lg:block">
            <div className="sticky top-24 overflow-hidden rounded-[28px] border border-gray-200/80 bg-white shadow-xl shadow-gray-200/40 dark:border-white/[0.06] dark:bg-[#111318] dark:shadow-none">

              <div className="border-b border-gray-100 p-6 dark:border-white/[0.06]">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400">
                    <ShoppingBag size={21} />
                  </div>

                  <div>
                    <h2 className="font-black text-gray-900 dark:text-white">
                      Sifariş xülasəsi
                    </h2>

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedCount} məhsul seçilib
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-6">

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    Məhsullar
                  </span>

                  <span className="font-bold text-gray-900 dark:text-white">
                    {selectedCount} ədəd
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    Ara cəm
                  </span>

                  <span className="font-bold text-gray-900 dark:text-white">
                    {calculateTotalOrginalPrice.toFixed(2)} ₼
                  </span>
                </div>

                {discountAmount > 0 && (
                  <div className="flex items-center justify-between rounded-xl bg-emerald-50 px-3 py-2.5 dark:bg-emerald-950/20">
                    <span className="flex items-center gap-2 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                      <Tag size={14} />
                      Endirim
                    </span>

                    <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                      -{discountAmount.toFixed(2)} ₼
                    </span>
                  </div>
                )}

                <div className="my-2 h-px bg-gray-100 dark:bg-white/[0.06]" />

                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                    Yekun məbləğ
                  </p>

                  <div className="mt-1 flex items-end justify-between gap-3">
                    <span className="text-3xl font-black tracking-tight text-gray-950 dark:text-white">
                      {calculateTotalPrice.toFixed(2)} ₼
                    </span>

                    {discountAmount > 0 && (
                      <span className="mb-1 flex items-center gap-1 text-xs font-bold text-emerald-500">
                        <Sparkles size={13} />
                        Qənaət
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleConfirmCart}
                  disabled={
                    !isStockAvailable ||
                    confirmingCart ||
                    selectedItems.length === 0
                  }
                  className={`flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-4 text-sm font-black transition-all ${
                    !isStockAvailable ||
                    confirmingCart ||
                    selectedItems.length === 0
                      ? "cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-white/10 dark:text-gray-600"
                      : "bg-gray-950 text-white shadow-xl shadow-gray-300/30 hover:-translate-y-0.5 hover:bg-black dark:bg-white dark:text-gray-950 dark:shadow-none dark:hover:bg-gray-100"
                  }`}
                >
                  {confirmingCart ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Hazırlanır...
                    </>
                  ) : (
                    <>
                      Sifarişi rəsmiləşdir
                      <ArrowRight size={17} />
                    </>
                  )}
                </button>

                {selectedItems.length === 0 && (
                  <p className="text-center text-[11px] font-medium text-gray-400">
                    Davam etmək üçün məhsul seçin
                  </p>
                )}

                {selectedItems.length > 0 &&
                  !isStockAvailable && (
                    <div className="rounded-2xl bg-amber-50 p-3 text-xs leading-5 text-amber-700 dark:bg-amber-950/20 dark:text-amber-300">
                      <div className="flex gap-2">
                        <AlertTriangle
                          size={15}
                          className="mt-0.5 shrink-0"
                        />

                        <span>
                          Seçilmiş məhsullardan biri
                          stokda yoxdur. Davam etmək
                          üçün onu seçimdən çıxarın.
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </aside>
        </div>

        {/* =====================================================
            MOBILE CHECKOUT BAR
        ====================================================== */}

        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200/80 bg-white/95 px-3 py-3 shadow-[0_-10px_40px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:border-white/[0.06] dark:bg-[#0d0e11]/95 lg:hidden">
          <div className="mx-auto max-w-7xl">

            {selectedItems.length > 0 &&
              !isStockAvailable && (
                <div className="mb-2 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-[10px] font-bold text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                  <AlertTriangle size={14} />
                  Stokda olmayan məhsulu seçimdən çıxarın
                </div>
              )}

            <div className="flex items-center gap-3">

              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  {selectedCount} məhsul seçilib
                </p>

                <div className="flex items-center gap-2">
                  <span className="text-xl font-black tracking-tight text-gray-950 dark:text-white">
                    {calculateTotalPrice.toFixed(2)} ₼
                  </span>

                  {discountAmount > 0 && (
                    <span className="text-[10px] font-bold text-emerald-500">
                      -{discountAmount.toFixed(2)} ₼
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={handleConfirmCart}
                disabled={
                  !isStockAvailable ||
                  confirmingCart ||
                  selectedItems.length === 0
                }
                className={`flex h-12 shrink-0 items-center justify-center gap-2 rounded-2xl px-5 text-xs font-black transition-all ${
                  !isStockAvailable ||
                  confirmingCart ||
                  selectedItems.length === 0
                    ? "bg-gray-200 text-gray-400 dark:bg-white/10 dark:text-gray-600"
                    : "bg-gray-950 text-white shadow-lg dark:bg-white dark:text-gray-950"
                }`}
              >
                {confirmingCart ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <>
                    Davam et
                    <ChevronRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* =====================================================
            MOBILE STOCK WARNING
        ====================================================== */}

        {selectedItems.length > 0 &&
          !isStockAvailable && (
            <div className="mt-4 hidden rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20 lg:block">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
                  <AlertTriangle size={17} />
                </div>

                <div>
                  <h3 className="text-sm font-black text-amber-800 dark:text-amber-300">
                    Səbəti təsdiqləmək mümkün deyil
                  </h3>

                  <p className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-400">
                    Seçdiyiniz məhsullardan ən azı
                    biri stokda yoxdur. Davam etmək
                    üçün həmin məhsulu seçimdən
                    çıxarın.
                  </p>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default Basket;